import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import Logo from '@/components/Logo';
import Panier from '@/components/Panier';
import ProductCard from '@/components/ProductCard';
import {
  useCatalogue,
  useCatalogueLines,
  useDebounced,
  useVueProduits,
  type Filters,
  type Product,
} from '@/lib/catalogue';
import {
  useAjouterAuPanier,
  useAnnulerCommande,
  useOrder,
  useOrderRewards,
  useSouvenirDuPanier,
} from '@/lib/orders';
import { cn } from '@/lib/utils';
import Confirmation from './Confirmation';

/**
 * Plan §5.4 — prise de commande. La page est bornée à la hauteur de l'écran
 * (h-dvh) : le catalogue défile DANS sa zone et le panier reste toujours visible,
 * en colonne de droite (paysage) ou en panneau bas (portrait). Sans ça, en
 * portrait, une longue liste de produits repoussait le panier hors de l'écran.
 */
export default function Commande() {
  const { id } = useParams();
  const orderId = Number(id);
  const navigate = useNavigate();
  const { data, isPending, isError, error } = useOrder(Number.isInteger(orderId) ? orderId : null);
  const order = data?.order;
  useSouvenirDuPanier(order);

  const [q, setQ] = useState('');
  const [lineCode, setLineCode] = useState<string | null>(null);
  const [vedettesSeules, setVedettesSeules] = useState(true); // Vedettes affichées par défaut
  const [revision, setRevision] = useState(false);
  const [vue, setVue] = useVueProduits();

  const debounced = useDebounced(q);
  // Les produits n'apparaissent qu'à la recherche, au choix d'une ligne, ou en mode
  // « Vedettes » — jamais tout le catalogue d'entrée de jeu.
  const montrerProduits = debounced.trim() !== '' || lineCode !== null || vedettesSeules;
  // La recherche cherche TOUJOURS dans tout le catalogue (de la ligne du rep) :
  // dès qu'on tape, on ignore le filtre « vedettes » — sinon taper un produit ne
  // renvoie rien s'il n'est pas vedette. Les vedettes ne filtrent que la vue par
  // défaut (sans recherche).
  const rechercheActive = debounced.trim() !== '';
  const filters: Filters = useMemo(
    () => ({
      q: debounced,
      lineCode,
      sort: 'pertinence',
      featuredOnly: vedettesSeules && !rechercheActive,
    }),
    [debounced, lineCode, vedettesSeules, rechercheActive],
  );
  const catalogue = useCatalogue(filters, montrerProduits);
  const lignes = useCatalogueLines();
  const ajouter = useAjouterAuPanier(orderId);
  const annuler = useAnnulerCommande(orderId);
  const rewards = useOrderRewards(orderId);

  function onAnnuler() {
    if (!confirm('Annuler cette commande ? Elle ne sera pas soumise et disparaîtra de la liste.')) {
      return;
    }
    annuler.mutate(undefined, { onSuccess: () => void navigate('/') });
  }

  if (isPending) {
    return <p className="text-muted-foreground p-8 text-center">Chargement de la commande…</p>;
  }
  if (isError || !order) {
    return (
      <div className="space-y-3 p-8 text-center">
        <p className="text-destructive font-medium">
          {(error as Error)?.message ?? 'Commande introuvable'}
        </p>
        <Link to="/" className="border-input inline-flex h-12 items-center rounded-lg border px-5">
          Retour à l'accueil
        </Link>
      </div>
    );
  }

  // Une commande déjà soumise (ou annulée) s'ouvre directement sur sa confirmation.
  if (order.status !== 'DRAFT' || revision) {
    return <Confirmation order={order} onRetour={() => setRevision(false)} />;
  }

  const mesLignes = lignes.data?.lines ?? [];

  return (
    <div className="flex h-dvh min-h-0 flex-col lg:flex-row">
      <section className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="bg-card space-y-3 border-b p-4">
          <div className="flex items-center gap-3">
            <Link to="/" className="border-input flex h-12 items-center rounded-lg border px-4 font-medium">
              ← Accueil
            </Link>
            <Logo className="h-8" />
            <span className="text-muted-foreground ml-auto text-sm">Commande en cours · brouillon</span>
          </div>

          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="N° de pièce ou description…"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            className="border-input bg-background focus:ring-ring h-16 w-full rounded-lg border px-4 text-lg focus:ring-2 focus:outline-none"
          />

          <div className="flex items-center gap-2">
            {/* Puces sur UNE rangée défilante en portrait (elles débordaient sur
                plusieurs rangées et écrasaient la liste produits) ; elles reviennent
                en multi-rangées sur grand écran. */}
            <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto lg:flex-wrap lg:overflow-visible">
            {/* Vedettes : affichées par défaut, toujours disponibles. */}
            <Chip
              active={vedettesSeules}
              onClick={() => {
                setVedettesSeules(true);
                setLineCode(null);
              }}
            >
              ★ Vedettes
            </Chip>
            {/* Une puce par ligne du rep — même s'il n'en a qu'UNE : elle permet
                d'afficher TOUS les produits de sa ligne d'un coup, sans rien taper. */}
            {mesLignes.map((l) => (
              <Chip
                key={l.id}
                active={!vedettesSeules && lineCode === l.code}
                onClick={() => {
                  setLineCode(lineCode === l.code ? null : l.code);
                  setVedettesSeules(false);
                }}
              >
                {l.code}
              </Chip>
            ))}

            </div>

            {/* Basculement grille / liste, comme au catalogue (préférence partagée). */}
            <div className="border-input flex shrink-0 overflow-hidden rounded-lg border">
              <button
                onClick={() => setVue('grid')}
                aria-pressed={vue === 'grid'}
                className={cn(
                  'h-10 px-3 text-sm font-medium',
                  vue === 'grid' ? 'bg-primary/10 text-foreground' : 'text-muted-foreground',
                )}
              >
                Grille
              </button>
              <button
                onClick={() => setVue('list')}
                aria-pressed={vue === 'list'}
                className={cn(
                  'border-input h-10 border-l px-3 text-sm font-medium',
                  vue === 'list' ? 'bg-primary/10 text-foreground' : 'text-muted-foreground',
                )}
              >
                Liste
              </button>
            </div>
          </div>
        </header>

        <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-4">
          {ajouter.isError && (
            <p className="text-destructive font-medium">{(ajouter.error as Error).message}</p>
          )}

          {!montrerProduits ? (
            <div className="rounded-xl border border-dashed p-10 text-center">
              <p className="font-medium">Recherchez un produit</p>
              <p className="text-muted-foreground mt-1 text-sm">
                Tapez un n° de pièce ou une description
                {mesLignes.length >= 1 ? ', choisissez une ligne' : ''}, ou affichez les ★ Vedettes.
              </p>
            </div>
          ) : (
            <>
              <p className="text-muted-foreground text-sm" aria-live="polite">
                {catalogue.isPending ? 'Chargement…' : `${catalogue.total} produit(s)`}
              </p>

              {vedettesSeules && !rechercheActive && !catalogue.isPending && catalogue.total === 0 ? (
                <div className="rounded-xl border border-dashed p-10 text-center">
                  <p className="font-medium">Aucune vedette pour l'instant</p>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Cherchez un produit ou choisissez une ligne. Les vedettes sont définies à
                    l'import ou par un administrateur.
                  </p>
                </div>
              ) : !catalogue.isPending && catalogue.total === 0 ? (
                <div className="rounded-xl border border-dashed p-10 text-center">
                  <p className="font-medium">Aucun produit trouvé</p>
                  <p className="text-muted-foreground mt-1 text-sm">
                    Essayez un autre n° de pièce ou une autre description.
                  </p>
                </div>
              ) : vue === 'list' ? (
                <div
                  data-testid="grille-produits"
                  className="bg-card divide-y overflow-hidden rounded-xl border"
                >
                  {catalogue.products.map((p: Product) => (
                    <ProductCard
                      key={p.id}
                      product={p}
                      view="list"
                      onSelect={() => ajouter.mutate({ productId: p.id })}
                    />
                  ))}
                </div>
              ) : (
                <div data-testid="grille-produits" className="grid gap-3 2xl:grid-cols-2">
                  {catalogue.products.map((p: Product) => (
                    <ProductCard
                      key={p.id}
                      product={p}
                      view="grid"
                      onSelect={() => ajouter.mutate({ productId: p.id })}
                    />
                  ))}
                </div>
              )}

              {catalogue.hasNextPage && (
                <button
                  onClick={() => void catalogue.fetchNextPage()}
                  disabled={catalogue.isFetchingNextPage}
                  className="border-input h-14 w-full rounded-lg border font-medium disabled:opacity-50"
                >
                  {catalogue.isFetchingNextPage ? 'Chargement…' : 'Afficher plus'}
                </button>
              )}
            </>
          )}
        </div>
      </section>

      {/* Panier : colonne droite en paysage, panneau bas borné en portrait — jamais
          hors écran. Il défile en interne, son pied (total + soumettre) reste visible. */}
      <section className="flex min-h-0 shrink-0 basis-1/2 flex-col border-t lg:h-auto lg:w-[28rem] lg:basis-auto lg:border-t-0 lg:border-l">
        <Panier
          order={order}
          onSoumettre={() => setRevision(true)}
          onAnnuler={onAnnuler}
          annulEnCours={annuler.isPending}
          rewards={rewards.data?.groups}
        />
      </section>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'h-12 shrink-0 rounded-lg border px-4 font-medium whitespace-nowrap',
        active ? 'border-primary bg-primary/10' : 'border-input',
      )}
    >
      {children}
    </button>
  );
}
